import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  Phone,
  Mail,
  MapPin,
  Clock,
  Plus,
  Check,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLang } from "@/context/LanguageContext";
import { COMPANY } from "@/lib/translations";
import { PROJECT_ASSETS } from "@/lib/projects";

const PROJECT_IMAGE_LEGACY =
  "https://customer-assets-agu9un31.emergentagent.net/job_70aa06d5-40fa-41d1-9a3c-79616c75b465/artifacts/3i1cmz72_photo_2026-07-16_17-18-06.jpg";
const HERO_BG = "https://images.unsplash.com/photo-1542621334-a254cf47733d?auto=format&fit=crop&w=1900&q=80";
const ABOUT_IMG = "https://images.pexels.com/photos/8961260/pexels-photo-8961260.jpeg?auto=compress&cs=tinysrgb&w=1600";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ HERO ============
const Hero = () => {
  const { t } = useLang();
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end pb-20 md:pb-32 pt-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#121212]/50 via-[#121212]/70 to-[#121212]" />
        <div className="absolute inset-0 grid-pattern opacity-40" />
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 w-full">
        {/* Overline */}
        <div className="flex items-center gap-3 mb-10 animate-fade-in">
          <span className="h-px w-12 bg-[#FF3B30]" />
          <p className="font-mono text-[11px] tracking-[0.3em] text-white/70">
            {t.hero.overline}
          </p>
        </div>

        {/* Title */}
        <h1
          className="font-display font-extrabold text-white mb-10 max-w-[1400px] animate-fade-up"
          style={{
            fontSize: "clamp(2.75rem, 10vw, 11rem)",
            lineHeight: 0.86,
          }}
        >
          <span className="block">{t.hero.titleA}</span>
          <span className="block text-white/60 font-light italic">
            {t.hero.titleB}
          </span>
          <span className="block">
            {t.hero.titleC}
            <span className="text-[#FF3B30]">.</span>
          </span>
        </h1>

        {/* Description + CTAs */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end animate-fade-up" style={{ animationDelay: "0.2s" }}>
          <p className="lg:col-span-5 text-white/70 text-base md:text-lg leading-relaxed max-w-xl">
            {t.hero.description}
          </p>

          <div className="lg:col-span-4 flex flex-wrap gap-3">
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#D63026] text-white font-mono text-[11px] tracking-[0.25em] px-6 py-4 transition-colors"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-3 border border-white/20 hover:border-white text-white font-mono text-[11px] tracking-[0.25em] px-6 py-4 transition-colors"
            >
              {t.hero.ctaSecondary}
            </a>
          </div>

          {/* Stats */}
          <div className="lg:col-span-3 flex gap-8 border-t border-white/10 pt-6 lg:border-t-0 lg:border-l lg:pl-8 lg:pt-0">
            {[
              { l: t.hero.stat1Label, v: t.hero.stat1Value },
              { l: t.hero.stat2Label, v: t.hero.stat2Value },
              { l: t.hero.stat3Label, v: t.hero.stat3Value },
            ].map((s) => (
              <div key={s.l} className="flex-1">
                <p className="font-display font-extrabold text-white text-xl md:text-2xl leading-none">
                  {s.v}
                </p>
                <p className="font-mono text-[9px] tracking-[0.25em] text-white/40 mt-2">
                  {s.l}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ MARQUEE ============
const Marquee = () => {
  const { t } = useLang();
  const items = [...t.marquee, ...t.marquee];
  return (
    <div className="border-y border-white/10 bg-[#0a0a0a] py-6 overflow-hidden">
      <div className="marquee-track flex gap-16 whitespace-nowrap">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-16">
            <span className="font-display font-light text-white/80 text-2xl md:text-3xl italic">
              {item}
            </span>
            <span className="text-[#FF3B30] text-lg">✦</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ SERVICES ============
const Services = () => {
  const { t } = useLang();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-40 relative"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-24">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#FF3B30]" />
              <p className="font-mono text-[11px] tracking-[0.3em] text-white/60">
                {t.services.overline}
              </p>
            </div>
          </div>
          <h2
            className="lg:col-span-8 font-display font-extrabold text-white leading-[0.9] tracking-tight"
            style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)" }}
          >
            {t.services.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {t.services.items.map((item, i) => (
            <div
              key={item.num}
              data-testid={`service-card-${i}`}
              className="group bg-[#121212] hover:bg-[#1c1c1e] p-8 md:p-12 transition-colors duration-500 relative"
            >
              <div className="flex items-start justify-between mb-10">
                <span className="font-mono text-[11px] tracking-[0.3em] text-white/40">
                  {item.num}
                </span>
                <Plus
                  size={20}
                  className="text-white/30 group-hover:text-[#FF3B30] group-hover:rotate-90 transition-all duration-500"
                  strokeWidth={1}
                />
              </div>

              <h3 className="font-display font-extrabold text-white text-3xl md:text-4xl lg:text-5xl mb-6 tracking-tight">
                {item.title}
              </h3>

              <p className="text-white/60 text-sm md:text-base leading-relaxed mb-8 max-w-md">
                {item.desc}
              </p>

              <ul className="space-y-2">
                {item.points.map((p) => (
                  <li
                    key={p}
                    className="flex items-center gap-3 text-white/70 text-sm"
                  >
                    <Check size={14} className="text-[#FF3B30]" strokeWidth={2} />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ PROJECTS ============
const Projects = () => {
  const { t } = useLang();
  const [filter, setFilter] = useState("alle");

  const disciplines = t.projects.disciplines;
  // Only show discipline chips that have projects
  const availableDisciplines = Object.keys(disciplines).filter((d) =>
    t.projects.list.some((p) => p.discipline === d)
  );

  const filtered =
    filter === "alle"
      ? t.projects.list
      : t.projects.list.filter((p) => p.discipline === filter);

  // Group by discipline (preserves list order within each group)
  const grouped = availableDisciplines
    .map((d) => ({
      key: d,
      label: disciplines[d],
      items: filtered.filter((p) => p.discipline === d),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="py-24 md:py-40 bg-[#0a0a0a] border-y border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 md:mb-16">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#FF3B30]" />
              <p className="font-mono text-[11px] tracking-[0.3em] text-white/60">
                {t.projects.overline}
              </p>
            </div>
          </div>
          <h2
            className="lg:col-span-8 font-display font-extrabold text-white leading-[0.9] tracking-tight"
            style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)" }}
          >
            {t.projects.title}
            <span className="text-[#FF3B30]">.</span>
          </h2>
        </div>

        {/* Filter chips */}
        <div
          className="flex flex-wrap items-center gap-3 mb-12 md:mb-16 pb-6 border-b border-white/10"
          data-testid="projects-filter"
        >
          <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 mr-2">
            {t.projects.filterLabel} →
          </p>
          <FilterChip
            active={filter === "alle"}
            onClick={() => setFilter("alle")}
            testId="projects-filter-alle"
            count={t.projects.list.length}
          >
            {t.projects.filterAll}
          </FilterChip>
          {availableDisciplines.map((d) => (
            <FilterChip
              key={d}
              active={filter === d}
              onClick={() => setFilter(d)}
              testId={`projects-filter-${d}`}
              count={t.projects.list.filter((p) => p.discipline === d).length}
            >
              {disciplines[d]}
            </FilterChip>
          ))}
        </div>

        {/* Grouped rows */}
        <div className="space-y-16 md:space-y-24">
          {grouped.map((group) => (
            <div key={group.key} data-testid={`projects-row-${group.key}`}>
              <div className="flex items-center gap-4 mb-8">
                <span className="font-mono text-[10px] tracking-[0.3em] text-[#FF3B30]">
                  {String(availableDisciplines.indexOf(group.key) + 1).padStart(
                    2,
                    "0"
                  )}
                </span>
                <h3 className="font-display font-extrabold text-white text-2xl md:text-3xl tracking-tight">
                  {group.label}
                </h3>
                <span className="font-mono text-[10px] tracking-[0.3em] text-white/40">
                  · {group.items.length}
                </span>
                <span className="flex-1 h-px bg-white/10" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {group.items.map((project, idx) => (
                  <ProjectCard key={project.slug} project={project} index={idx} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="mt-16 md:mt-20 font-mono text-[11px] tracking-[0.3em] text-white/40 text-center">
          ↓ {t.projects.viewAll}
        </p>
      </div>
    </section>
  );
};

const FilterChip = ({ active, onClick, children, count, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`group inline-flex items-center gap-2 px-4 py-2 border transition-colors duration-300 font-mono text-[11px] tracking-[0.2em] ${
      active
        ? "bg-[#FF3B30] border-[#FF3B30] text-white"
        : "border-white/15 hover:border-white text-white/70 hover:text-white"
    }`}
  >
    <span>{children}</span>
    <span
      className={`text-[9px] ${
        active ? "text-white/80" : "text-white/40 group-hover:text-white/60"
      }`}
    >
      [{String(count).padStart(2, "0")}]
    </span>
  </button>
);

const ProjectCard = ({ project, index }) => {
  const asset = PROJECT_ASSETS[project.slug];
  const externalUrl = asset?.externalUrl;

  const cardInner = (
    <>
      <div className="relative overflow-hidden aspect-[4/3] bg-[#1c1c1e]">
        <img
          src={asset?.image}
          alt={project.title}
          loading={index === 0 ? "eager" : "lazy"}
          className="w-full h-full object-cover object-center transition-transform duration-[1200ms] group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/60 via-transparent to-transparent" />
        <div className="absolute top-5 left-5 flex items-center gap-2">
          <span className="bg-[#FF3B30] text-white font-mono text-[10px] tracking-[0.25em] px-3 py-1.5">
            {project.num} · {project.year}
          </span>
          <span className="bg-black/60 backdrop-blur-sm border border-white/20 text-white/90 font-mono text-[10px] tracking-[0.25em] px-3 py-1.5">
            {project.category}
          </span>
        </div>
        <div className="absolute bottom-5 right-5 h-12 w-12 md:h-14 md:w-14 bg-white text-black flex items-center justify-center group-hover:bg-[#FF3B30] group-hover:text-white transition-colors duration-500">
          <ArrowUpRight size={22} strokeWidth={1.5} />
        </div>
      </div>

      <div className="pt-6 md:pt-8 flex flex-col flex-1">
        <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 mb-3">
          {project.location}
        </p>
        <h3 className="font-display font-extrabold text-white text-2xl md:text-3xl leading-tight tracking-tight mb-4 group-hover:text-[#FF3B30] transition-colors duration-500">
          {project.title}
        </h3>
        <p className="text-white/60 text-sm leading-relaxed mb-6">
          {project.role}
        </p>

        <div className="mt-auto inline-flex items-center gap-3 text-white font-mono text-[11px] tracking-[0.25em] border-b border-white/20 pb-2 self-start group-hover:border-[#FF3B30] group-hover:text-[#FF3B30] transition-colors">
          {project.cta}
          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </>
  );

  if (externalUrl) {
    return (
      <a
        href={externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`project-card-${project.slug}`}
        className="group flex flex-col"
      >
        {cardInner}
      </a>
    );
  }

  return (
    <Link
      to={`/projekte/${project.slug}`}
      data-testid={`project-card-${project.slug}`}
      className="group flex flex-col"
    >
      {cardInner}
    </Link>
  );
};

// ============ ABOUT ============
const About = () => {
  const { t } = useLang();
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-40"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden bg-[#1c1c1e]">
              <img
                src={ABOUT_IMG}
                alt="Engineers reviewing plans"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#FF3B30] text-white p-6 md:p-8 max-w-[220px]">
              <p className="font-display font-extrabold text-4xl md:text-5xl leading-none">
                04
              </p>
              <p className="font-mono text-[10px] tracking-[0.25em] mt-3">
                {t.hero.stat1Label}
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#FF3B30]" />
              <p className="font-mono text-[11px] tracking-[0.3em] text-white/60">
                {t.about.overline}
              </p>
            </div>
            <h2
              className="font-display font-extrabold text-white leading-[0.9] tracking-tight mb-10"
              style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)" }}
            >
              {t.about.title}
            </h2>
            <div className="space-y-5 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
              <p>{t.about.p1}</p>
              <p>{t.about.p2}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 pt-10 border-t border-white/10">
              {t.about.pillars.map((p) => (
                <div key={p.label}>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-white/40 mb-2">
                    {p.label}
                  </p>
                  <p className="font-display font-extrabold text-white text-lg md:text-xl">
                    {p.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ CONTACT ============
const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t.contact.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-40 bg-[#0a0a0a] border-t border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#FF3B30]" />
              <p className="font-mono text-[11px] tracking-[0.3em] text-white/60">
                {t.contact.overline}
              </p>
            </div>
            <h2
              className="font-display font-extrabold text-white leading-[0.9] tracking-tight mb-8"
              style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)" }}
            >
              {t.contact.title}
              <br />
              <span className="italic font-light text-white/60">
                {t.contact.titleAccent}
              </span>
            </h2>
            <p className="text-white/70 text-base leading-relaxed mb-12 max-w-md">
              {t.contact.description}
            </p>

            <div className="space-y-6">
              <ContactInfoRow
                icon={<Phone size={18} strokeWidth={1.5} />}
                label={t.contact.info.phoneLabel}
                value={COMPANY.phone}
                href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                testId="contact-info-phone"
              />
              <ContactInfoRow
                icon={<Mail size={18} strokeWidth={1.5} />}
                label={t.contact.info.emailLabel}
                value={COMPANY.email}
                href={`mailto:${COMPANY.email}`}
                testId="contact-info-email"
              />
              <ContactInfoRow
                icon={<MapPin size={18} strokeWidth={1.5} />}
                label={t.contact.info.addressLabel}
                value={`${COMPANY.addressLine1}, ${COMPANY.addressLine2}`}
                testId="contact-info-address"
              />
              <ContactInfoRow
                icon={<Clock size={18} strokeWidth={1.5} />}
                label={t.contact.info.hoursLabel}
                value={t.contact.info.hoursValue}
                testId="contact-info-hours"
              />
            </div>
          </div>

          {/* Right — Form */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-[#121212] border border-white/10 p-8 md:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <FormField
                label={t.contact.form.name}
                name="name"
                value={form.name}
                onChange={onChange}
                required
                testId="contact-input-name"
              />
              <FormField
                label={t.contact.form.email}
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                required
                testId="contact-input-email"
              />
            </div>
            <div className="mb-6">
              <FormField
                label={t.contact.form.subject}
                name="subject"
                value={form.subject}
                onChange={onChange}
                required
                testId="contact-input-subject"
              />
            </div>
            <div className="mb-8">
              <label className="block font-mono text-[10px] tracking-[0.25em] text-white/50 mb-3">
                {t.contact.form.message}
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={6}
                required
                data-testid="contact-input-message"
                className="w-full bg-transparent border border-white/15 hover:border-white/30 focus:border-[#FF3B30] focus:outline-none text-white px-4 py-3 text-sm transition-colors resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="group inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#D63026] disabled:bg-white/10 disabled:text-white/40 disabled:cursor-not-allowed text-white font-mono text-[11px] tracking-[0.25em] px-8 py-4 transition-colors"
            >
              {sending ? t.contact.form.sending : t.contact.form.submit}
              <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const ContactInfoRow = ({ icon, label, value, href, testId }) => {
  const content = (
    <div
      data-testid={testId}
      className="flex items-start gap-4 group border-t border-white/10 pt-6"
    >
      <span className="text-[#FF3B30] mt-1">{icon}</span>
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-white/40 mb-1">
          {label}
        </p>
        <p className="text-white text-sm md:text-base group-hover:text-[#FF3B30] transition-colors">
          {value}
        </p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block">
      {content}
    </a>
  ) : (
    content
  );
};

const FormField = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="block font-mono text-[10px] tracking-[0.25em] text-white/50 mb-3">
      {label}
    </span>
    <input
      data-testid={testId}
      {...props}
      className="w-full bg-transparent border border-white/15 hover:border-white/30 focus:border-[#FF3B30] focus:outline-none text-white px-4 py-3 text-sm transition-colors"
    />
  </label>
);

// ============ HOME PAGE ============
const Home = () => {
  const { lang } = useLang();
  useEffect(() => {
    // scroll to hash if present
    if (window.location.hash) {
      const id = window.location.hash.replace("#", "");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    }
  }, [lang]);

  return (
    <div data-testid="home-page" className="bg-[#121212] text-white">
      <Navbar />
      <main>
        <Hero />
        <Marquee />
        <Services />
        <Projects />
        <About />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

export default Home;
